import { createSelector } from 'reselect';
import utils from 'utils';
import { appProgressBarLocationInitiatePath } from 'paths/app';
import {
  getInitiatePickupTime,
  getInitiateReturnTime,
  getModifyFlag,
  getReservationReturnLocation,
  getReservationPickupLocation,
  getOneWayRentalFlag,
} from 'selectors/reservationSelectors';
import { getReservationInitiateError } from 'selectors/errorSelectors';

import { CURSORS } from 'constants';

// TODO: add paths to paths files instead of using directly on the selectors
const getPickupLocation = (state) =>
  utils.safeToJS(state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'pickup_location']), null);
const getReturnLocation = (state) =>
  utils.safeToJS(state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'return_location']), null);
const getOneWayRental = (state) => state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'one_way_rental']);

export const getInitialPickupLocation = (state) =>
  utils.safeToJS(state.getIn(['gmi', 'session', 'gma', 'reservation', 'initiate_request', 'pickup_location']), null);
export const getInitialReturnLocation = (state) =>
  utils.safeToJS(state.getIn(['gmi', 'session', 'gma', 'reservation', 'initiate_request', 'return_location']), null);
const getInitialOneWayRental = (state) =>
  state.getIn(['gmi', 'session', 'gma', 'reservation', 'initiate_request', 'one_way_rental']);

export const getInitialCarClassCode = (state) =>
  state.getIn(['gmi', 'session', 'gma', 'initiate_request', 'car_class_code']);

//  We if we havn't picked a branch but just a time, the times live under ['gmi', 'session', 'gma', 'initiate_request', 'pickup_time'] not ['gmi', 'ui', 'reservations', 'initiate', 'pickup_time'].
export const getPickupTime = (state) =>
  state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'pickup_time']) ||
  state.getIn(['gmi', 'session', 'gma', 'initiate_request', 'pickup_time']);
export const getReturnTime = (state) =>
  state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'return_time']) ||
  state.getIn(['gmi', 'session', 'gma', 'initiate_request', 'return_time']);

const getPickupLocationHours = (state) =>
  state.getIn(
    CURSORS.locationHours(state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'pickup_location', 'id'])),
    false
  );
const getReturnLocationHours = (state) =>
  state.getIn(
    CURSORS.locationHours(state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'return_location', 'id'])),
    false
  );

export const locationsSelector = createSelector(
  [getPickupLocation, getReturnLocation, getOneWayRental],
  (pickupLocation, returnLocation, oneWayRental) => ({
    pickupLocation,
    returnLocation,
    oneWayRental,
  })
);

export const locationsInitialValuesSelector = createSelector(
  [getInitialPickupLocation, getInitialReturnLocation, getInitialOneWayRental],
  (pickupLocation, returnLocation, oneWayRental) => ({
    pickupLocation,
    returnLocation,
    oneWayRental,
  })
);

export const reservationInitiateLocationSelector = createSelector(
  [getReservationPickupLocation, getReservationReturnLocation, getOneWayRentalFlag],
  (pickupLocation, returnLocation, oneWayRental) => ({
    gboPickupLocation: pickupLocation,
    gboReturnLocation: returnLocation,
    gboRentalFlag: oneWayRental,
  })
);

export const initiateLocationSelector = createSelector(
  [
    reservationInitiateLocationSelector,
    getPickupLocation,
    getReturnLocation,
    getOneWayRental,
    getInitialPickupLocation,
    getInitialReturnLocation,
    getInitialOneWayRental,
  ],
  (location, pickupLocation, returnLocation, oneWayRental, gmaPickupLocation, gmaReturnLocation, gmaOneWayRental) => {
    const { gboPickupLocation, gboReturnLocation } = location || {};

    return {
      pickupLocation:
        pickupLocation || (gboPickupLocation.name !== gmaPickupLocation?.name ? gboPickupLocation : gmaPickupLocation),
      returnLocation:
        returnLocation || (gboReturnLocation.name !== gmaReturnLocation?.name ? gboReturnLocation : gmaReturnLocation),
      oneWayRental: oneWayRental || gmaOneWayRental,
    };
  }
);

export const dateTimeValuesSelector = createSelector([getPickupTime, getReturnTime], (pickup_time, return_time) => {
  const [pickupDate, pickupTime] = pickup_time.split('T');
  const [returnDate, returnTime] = return_time.split('T');

  return {
    pickupDate,
    pickupTime,
    returnDate,
    returnTime,
  };
});

export const dateTimeInitiateRequestValuesSelector = createSelector(
  [getInitiatePickupTime, getInitiateReturnTime],
  (pickup_time, return_time) => {
    const [pickupDate, pickupTime] = pickup_time ? pickup_time.split('T') : [''];
    const [returnDate, returnTime] = return_time ? return_time.split('T') : [''];

    return {
      pickupDate,
      pickupTime,
      returnDate,
      returnTime,
    };
  }
);

// Selector to get gmi > ui, to match what's in gbo
export const isInitiateDateTimeisChanged = createSelector(
  [
    getInitiatePickupTime,
    getInitiateReturnTime,
    getPickupTime,
    getReturnTime,
    getModifyFlag,
    getReservationInitiateError,
  ],
  (
    pickup_time_initiate_request,
    return_time_initiate_request,
    pickup_time_initiate,
    return_time_initiate,
    isTrueModify,
    reservationInitiateError
  ) =>
    isTrueModify &&
    reservationInitiateError.length > 0 &&
    (pickup_time_initiate !== pickup_time_initiate_request || return_time_initiate !== return_time_initiate_request)
);

// Selector to get gmi > ui, to match what's in gbo
export const initiateLocationModifiedSelector = createSelector(
  [
    getPickupLocation,
    getReturnLocation,
    getOneWayRental,
    getInitialPickupLocation,
    getInitialReturnLocation,
    getInitialOneWayRental,
    getModifyFlag,
    getReservationInitiateError,
  ],
  (
    pickupLocation,
    returnLocation,
    oneWayRental,
    gmaPickupLocation,
    gmaReturnLocation,
    gmaOneWayRental,
    isTrueModify,
    reservationInitiateError
  ) =>
    isTrueModify &&
    reservationInitiateError.length > 0 &&
    (pickupLocation !== gmaPickupLocation || returnLocation !== gmaReturnLocation || oneWayRental !== gmaOneWayRental)
);

export const locationHoursSelector = createSelector(
  [getPickupLocationHours, getReturnLocationHours],
  (pickupEligibility, returnEligibility) => ({
    pickupEligibility: utils.safeToJS(pickupEligibility, {}),
    returnEligibility: utils.safeToJS(returnEligibility, {}),
  })
);

export const getProgressBarLocationInitiateSelector = (state) => state.getIn(appProgressBarLocationInitiatePath);
